import type { ComboboxOption } from "@v1/components/designSystem/combobox/ComboboxContext";
import type { ProductIdsWithVariantIds } from "replo-runtime/shared/types";
import type { ProductRef } from "schemas/product";

import * as React from "react";

import { Combobox } from "@v1/components/designSystem/combobox/Combobox";
import {
  ProductSelectionButton,
  ProductSelectionCombobox,
} from "@v1/components/editor/page/ProductSelectionCombobox";
import { useSpecificStoreProducts } from "@v1/hooks/useStoreProducts";
import { getVariantLabel } from "@v1/utils/string";
import mapValues from "lodash-es/mapValues";
import pickBy from "lodash-es/pickBy";
import { parseInteger } from "replo-utils/lib/math";
import { pluralize } from "replo-utils/lib/pluralize";

type ProductVariantSelectionComboboxProps = {
  value: ProductIdsWithVariantIds | null;
  onSubmit: (value: ProductIdsWithVariantIds | null) => void;
  align?: "start" | "center" | "end";
};

export const ProductVariantSelectionCombobox: React.FC<
  ProductVariantSelectionComboboxProps
> = ({ value, onSubmit, align = "start" }) => {
  const productIds = Object.keys(value ?? {});

  const { products } = useSpecificStoreProducts(productIds);

  const selectedProductRefs = React.useMemo((): ProductRef[] => {
    return productIds.map((id) => ({ productId: parseInteger(id) }));
  }, [productIds]);

  const handleProductChange = React.useCallback(
    (productRefs: ProductRef[]) => {
      if (productRefs.length === 0) {
        onSubmit(null);
        return;
      }

      const newProductIds = productRefs.map((ref) => ref.productId.toString());

      const newValue = newProductIds.reduce<ProductIdsWithVariantIds>(
        (acc, productId) => {
          // Keep existing variant selections if product is already selected
          if (value && value[productId] !== undefined) {
            acc[productId] = value[productId];
          } else {
            // Default to all variants selected (null) for new products
            acc[productId] = null;
          }
          return acc;
        },
        {},
      );

      onSubmit(newValue);
    },
    [onSubmit, value],
  );

  const { comboboxOptions, allVariantIdsByProduct } = React.useMemo(() => {
    const options: ComboboxOption[] = [];
    const variantsMap: Record<string, string[]> = {};

    products.forEach((product) => {
      variantsMap[product.id.toString()] = product.variants.map((v) => {
        return v.id.toString();
      });
      product.variants.forEach((variant) => {
        const variantLabel = getVariantLabel(variant);
        options.push({
          value: variant.id.toString(),
          label: variantLabel,
          groupTitle: product.title,
          tooltip: variantLabel.length > 28 ? variantLabel : null,
        });
      });
    });
    return { comboboxOptions: options, allVariantIdsByProduct: variantsMap };
  }, [products]);

  const selectedVariantIds = React.useMemo(() => {
    if (!value) {
      return [];
    }
    return Object.entries(value).flatMap(([productId, variantIds]) => {
      // If null, all variants for this product are selected
      if (variantIds === null) {
        return allVariantIdsByProduct[productId] ?? [];
      }
      return variantIds;
    });
  }, [value, allVariantIdsByProduct]);

  const handleVariantChange = React.useCallback(
    (newSelectedVariantIds: string[]) => {
      const mappedValues = mapValues(
        allVariantIdsByProduct,
        (allVariantsForProduct) => {
          const selectedVariantsForProduct = newSelectedVariantIds.filter(
            (id) => allVariantsForProduct.includes(id),
          );

          if (selectedVariantsForProduct.length === 0) {
            // Signal to pickBy to remove this entry
            return undefined;
          }

          if (
            selectedVariantsForProduct.length === allVariantsForProduct.length
          ) {
            // All variants selected
            return null;
          }
          // Some variants selected
          return selectedVariantsForProduct;
        },
      );

      // Filter out products where no variants were selected
      const newValue = pickBy(mappedValues, (value) => value !== undefined);

      onSubmit(Object.keys(newValue).length > 0 ? newValue : null);
    },
    [allVariantIdsByProduct, onSubmit],
  );

  return (
    <div className="flex flex-col gap-2">
      {/* Product Selection */}
      <ProductSelectionCombobox
        align={align}
        value={selectedProductRefs}
        onChange={handleProductChange}
        isMultiselect={true}
        trigger={
          <ProductSelectionButton
            size="base"
            title={`${selectedProductRefs.length} ${pluralize({ singular: "product", plural: "products", count: selectedProductRefs.length })}`}
          />
        }
      />

      {/* Variant Selection */}
      {selectedProductRefs.length > 0 && (
        <Combobox.Root
          options={comboboxOptions}
          value={selectedVariantIds}
          onChange={handleVariantChange}
          isMultiselect={true}
        >
          <Combobox.Trigger>
            <ProductSelectionButton
              size="base"
              title={`${selectedVariantIds.length} ${pluralize({ singular: "variant", plural: "variants", count: selectedVariantIds.length })}`}
            />
          </Combobox.Trigger>
          <Combobox.Popover
            // NOTE (Jackson, 2025-04-08): This component should probably
            // be a Menu design-system wise, this in effect is the same thing
            unsafe_className="pt-1.5"
            layoutClassName="max-h-[400px] w-[240px]"
            align={align}
          >
            <Combobox.Content
              areOptionsSearchable={comboboxOptions.length > 10}
              inputPlaceholder="Search variants..."
              emptySearchMessage="No variants found."
            />
          </Combobox.Popover>
        </Combobox.Root>
      )}
    </div>
  );
};
